/**
 * Infinite scroll with vehicle/universal split + COMPLETE prefetch using real totals.
 * - Uses data-total-pages/data-current-page from Liquid for exact coverage.
 * - Fetches pages 2..N in PARALLEL (order-preserving) for speed.
 * - Dedupes by [data-product-handle].
 * - Vehicles remain in the main grid; Universals in a second grid after it (vehicles always first).
 * - Locks the inline promo card to the 3rd VEHICLE position (after 2nd vehicle product).
 * - Works on single-page collections (no pagination) and ignores search results.
 *
 * Requires card-product.liquid to output:
 *   [data-fitment="vehicle"|"universal"] and [data-product-handle="..."] on the card root.
 */

if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor(){ super(); this.init(); }
    disconnectedCallback(){ this.destroy(); }

    init(){
      this.paginationContainer = this.querySelector('.js-pagination');
      this.itemsContainer      = this._findItemsContainer();
      this.paginationItems     = this.querySelectorAll('.js-pagination__item');
      this.nextButton          = this.querySelector('.js-pagination__next-button');

      // Settings
      this.prefetchAll       = (this.dataset.prefetchAll === 'true') || (window.FITMENT_PREFETCH_ALL === true);
      this.deferUniversal    = this.dataset.deferUniversal !== 'false';
      this.concurrency       = parseInt(this.dataset.prefetchConcurrency || '3', 10); if (!this.concurrency || this.concurrency < 1) this.concurrency = 3;
      this.maxPrefetchPages  = parseInt(this.dataset.maxPrefetchPages || '500', 10);

      // Totals from Liquid (optional but recommended)
      this.totalPages  = parseInt(this.dataset.totalPages || '0', 10) || 0;
      this.currentPage = parseInt(this.dataset.currentPage || this._getCurrentPageFromUrl() || '1', 10) || 1;

      // Dedupe by handle
      this.seenHandles = new Set();

      if (!this.itemsContainer) return;

      // Create universal bucket & split initial items
      this._ensureUniversalBucket();
      this._partitionExistingItemsIntoBucketsAndRegister();

      // Promo pinning: run now and keep it pinned
      this._repositionPromoToVehicleIndex(2);
      this._observeAndLockPromo(2);
      // tiny post-load retries to beat other scripts
      requestAnimationFrame(() => this._repositionPromoToVehicleIndex(2));
      setTimeout(() => this._repositionPromoToVehicleIndex(2), 60);
      window.addEventListener('load', () => this._repositionPromoToVehicleIndex(2));

      // If there’s no next URL (single page), stop here
      const hasNextHref = this.nextButton && this.nextButton.getAttribute && this.nextButton.getAttribute('href');
      if (!this.paginationContainer || !hasNextHref) return;

      // Normal infinite scroll listeners
      this.bindListeners();

      // Prefetch all pages
      if (this.prefetchAll && this.totalPages > this.currentPage) {
        if (this.intersectionObserver) { this.intersectionObserver.disconnect(); this.intersectionObserver = null; }
        this._prefetchAllUsingTotals();
      }
    }

    destroy(){
      clearInterval(this.nextButtonTimer);
      if (this.nextButton && this.clickHandler) this.nextButton.removeEventListener('click', this.clickHandler);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.promoObserver) this.promoObserver.disconnect();
    }

    bindListeners(){
      const self = this;
      this.clickHandler = function(e){ e.preventDefault(); self.fetchNextItems(); };
      if (this.nextButton) this.nextButton.addEventListener('click', this.clickHandler);

      if (this.dataset.infiniteScrollEnabled === 'true' && !this.prefetchAll && this.paginationContainer) {
        this.intersectionObserver = new IntersectionObserver(function(entries){
          entries.forEach(function(entry){
            if (entry.isIntersecting) {
              self.fetchNextItems();
              self.nextButtonTimer = setInterval(function(){ self.fetchNextItems(); }, 1500);
            } else {
              clearInterval(self.nextButtonTimer);
            }
          });
        }, { rootMargin: String(window.innerHeight) + 'px 0px' });
        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    async fetchNextItems(){
      if (!this.nextButton || !this.nextButton.getAttribute('href') || this.fetchInProgress) return;

      var originalButtonText = this.nextButton.textContent;
      this.fetchInProgress = true;
      this.nextButton.setAttribute('aria-disabled','true');
      var loadingText = (window.strings && window.strings.general && window.strings.general.pagination && window.strings.general.pagination.loadingText) || 'Loading…';
      this.nextButton.textContent = loadingText;

      try{
        var href = this.nextButton.getAttribute('href');
        var html = await this._fetchText(href);
        var t    = document.createElement('template'); t.innerHTML = html;

        this._appendItemsFromFragment(t.content);
        this._updatePaginationControlsFromTemplate(t.content);
        this._afterAppend();
      } catch(err){
        console.error('[infinite-scroll] load error:', err);
      } finally {
        this.fetchInProgress = false;
        if (this.nextButton){
          this.nextButton.removeAttribute('aria-disabled');
          this.nextButton.textContent = originalButtonText;
        }
      }
    }

    /** Prefetch all using totals **/
    async _prefetchAllUsingTotals(){
      try{
        var loadingText = (window.strings && window.strings.general && window.strings.general.pagination && window.strings.general.pagination.loadingText) || 'Loading…';
        this.nextButton.textContent = loadingText;
      }catch(e){}

      var pattern      = this._buildPageUrlPattern(this.nextButton.getAttribute('href') || window.location.href);
      var firstToFetch = this.currentPage + 1;
      var lastToFetch  = Math.min(this.totalPages, this.currentPage + this.maxPrefetchPages);

      var pages = [];
      for (var p = firstToFetch; p <= lastToFetch; p++) pages.push(p);
      if (!pages.length) { this._cleanupAfterLastPage(); return; }

      var queue = pages.map(function(pageNum, idx){ return { page: pageNum, index: idx, url: pattern(pageNum) }; });
      var inFlight = 0, results = {}, nextToFlush = 0;
      var self = this;

      function pump(){
        while (inFlight < self.concurrency && queue.length){
          var job = queue.shift();
          inFlight++;
          (function(pageNum, index, url){
            fetch(url, { credentials:'same-origin' })
              .then(function(r){ if (!r.ok) throw new Error('HTTP '+r.status); return r.text(); })
              .then(function(html){
                var t = document.createElement('template'); t.innerHTML = html;
                results[index] = t;
                flush();
              })
              .catch(function(err){
                console.error('[prefetch] page', pageNum, 'failed:', err);
                results[index] = null;
                flush();
              })
              .finally(function(){ inFlight--; if (queue.length) pump(); });
          })(job.page, job.index, job.url);
        }
      }

      function flush(){
        while (results.hasOwnProperty(nextToFlush)){
          var t = results[nextToFlush];
          if (t){
            var frags = self._splitItemsByFitmentDedup(t.content);
            if (frags){
              if (frags.v && frags.v.childNodes.length) self.itemsContainer.appendChild(frags.v);
              if (self.deferUniversal) {
                if (!self._universalQueueFrag) self._universalQueueFrag = document.createDocumentFragment();
                if (frags.u && frags.u.childNodes.length) self._universalQueueFrag.appendChild(frags.u);
              } else {
                if (frags.u && frags.u.childNodes.length) self.universalBucket.appendChild(frags.u);
              }
            }
            self._updatePaginationControlsFromTemplate(t.content, true);
            self._afterAppend();
          }
          delete results[nextToFlush];
          nextToFlush++;
        }

        if (nextToFlush === pages.length && inFlight === 0 && queue.length === 0){
          if (self.deferUniversal && self._universalQueueFrag && self._universalQueueFrag.childNodes.length){
            self.universalBucket.appendChild(self._universalQueueFrag);
            self._universalQueueFrag = null;
          }
          self._cleanupAfterLastPage();
        }
      }

      pump();
    }

    /* ---------- Promo locking ---------- */

    _observeAndLockPromo(desiredVehicleIndex){
      if (document.body && document.body.classList.contains('template-search')) return;
      if (!this.itemsContainer) return;

      if (this.promoObserver) this.promoObserver.disconnect();
      let rafId = null;
      this.promoObserver = new MutationObserver(() => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => this._repositionPromoToVehicleIndex(desiredVehicleIndex));
      });
      this.promoObserver.observe(this.itemsContainer, { childList: true });
    }

    _repositionPromoToVehicleIndex(desiredIndex){
      try{
        if (document.body && document.body.classList.contains('template-search')) return;
        if (!this.itemsContainer) return;

        const itemEls = this._getDirectItems();
        if (!itemEls.length) return;

        const promoItem = this._findInlinePromoItem(itemEls);
        if (!promoItem) return;

        const vehicles = itemEls.filter(el => this._isProductItem(el) && this._isVehicleItem(el));
        if (!vehicles.length) return;

        let targetIdx = (typeof desiredIndex === 'number') ? desiredIndex : 2; // after 2nd vehicle => index 2
        if (targetIdx < 0) targetIdx = 0;
        if (targetIdx > vehicles.length - 1) targetIdx = vehicles.length - 1;

        const targetVehicle = vehicles[targetIdx];
        if (!targetVehicle) return;

        // Only move if needed
        const currentIndex = itemEls.indexOf(promoItem);
        // compute where promo SHOULD be in DOM: after targetVehicle
        const desiredNextSibling = targetVehicle.nextSibling;
        if (promoItem === desiredNextSibling) return;

        // Temporarily stop observing while we move
        if (this.promoObserver) this.promoObserver.disconnect();
        if (desiredNextSibling) {
          this.itemsContainer.insertBefore(promoItem, desiredNextSibling);
        } else {
          this.itemsContainer.appendChild(promoItem);
        }
        // restart observer
        this._observeAndLockPromo(targetIdx);
      } catch(e){
        console.warn('[promo move] failed:', e);
      }
    }

    _getDirectItems(){
      return Array.prototype.slice.call(
        this.itemsContainer.querySelectorAll(':scope > .js-pagination__item')
      );
    }

    _isProductItem(el){
      return !!el.querySelector('[data-product-handle]');
    }

    _isVehicleItem(el){
      const card = el.querySelector('[data-fitment]');
      const fit  = card ? card.getAttribute('data-fitment') : 'vehicle';
      return fit === 'vehicle';
    }

    _findInlinePromoItem(itemEls){
      for (let i=0;i<itemEls.length;i++){
        const it = itemEls[i];
        // Ignore true product cards
        if (this._isProductItem(it)) continue;

        // Must have an inline promo marker inside
        const innerPromo = it.querySelector(
          '.collection-inline-promo .promo-card, ' +
          '.collection-inline-promo [data-promo-card], ' +
          '.promo-card-wrapper, ' +
          '[data-inline-promo], ' +
          '.promo-card, ' +
          '.promo-image'
        );
        if (!innerPromo) continue;

        // Skip if this is a banner/top promo wrapper
        if (innerPromo.closest('.collection-top-promo') || innerPromo.closest('.collection-banner')) continue;

        // Found the inline promo tile
        return it;
      }
      return null;
    }

    /* ---------- Core helpers ---------- */

    _getCurrentPageFromUrl(){
      try{ const u=new URL(window.location.href); const qp=u.searchParams.get('page'); return qp?parseInt(qp,10):1; }catch(e){ return 1; }
    }

    _findItemsContainer(){
      const any = this.querySelector('.js-pagination__item');
      return any ? any.parentElement : null;
    }

    _ensureUniversalBucket(){
      if (this.universalBucket && document.body.contains(this.universalBucket)) return;
      const bucket = document.createElement('div');
      bucket.className = this.itemsContainer.className;
      bucket.setAttribute('data-fitment-bucket','universal');

      const parent = this.itemsContainer.parentNode;
      if (parent){
        if (this.paginationContainer && this.paginationContainer.parentNode===parent) parent.insertBefore(bucket, this.paginationContainer);
        else if (this.itemsContainer.nextSibling) parent.insertBefore(bucket, this.itemsContainer.nextSibling);
        else parent.appendChild(bucket);
      } else {
        this.appendChild(bucket);
      }
      this.universalBucket = bucket;
    }

    _partitionExistingItemsIntoBucketsAndRegister(){
      let items = this._getDirectItems();
      if (!items.length) return;

      for (let i=0;i<items.length;i++){
        const it = items[i];
        const handle = this._extractHandle(it);
        if (handle) this.seenHandles.add(handle);
        if (this._isUniversalItem(it)) this.universalBucket.appendChild(it);
      }
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
    }

    _appendItemsFromFragment(fragment){
      const frags = this._splitItemsByFitmentDedup(fragment);
      if (!frags) return;
      if (frags.v && frags.v.childNodes.length) this.itemsContainer.appendChild(frags.v);
      if (this.deferUniversal && this.prefetchAll){
        if (!this._universalQueueFrag) this._universalQueueFrag = document.createDocumentFragment();
        if (frags.u && frags.u.childNodes.length) this._universalQueueFrag.appendChild(frags.u);
      } else {
        if (frags.u && frags.u.childNodes.length) this.universalBucket.appendChild(frags.u);
      }

      // Re-lock promo after new items arrive
      this._repositionPromoToVehicleIndex(2);
    }

    _splitItemsByFitmentDedup(fragment){
      const vFrag=document.createDocumentFragment(), uFrag=document.createDocumentFragment();
      let items = Array.prototype.slice.call(fragment.querySelectorAll('.js-pagination__item'));
      if (!items.length) return null;

      for (let i=0;i<items.length;i++){
        const it=items[i];
        const handle=this._extractHandle(it);
        if (handle && this.seenHandles.has(handle)) continue;
        if (handle) this.seenHandles.add(handle);
        if (this._isUniversalItem(it)) uFrag.appendChild(it);
        else vFrag.appendChild(it);
      }
      return {v:vFrag, u:uFrag};
    }

    _extractHandle(itemEl){
      const card = itemEl.querySelector('[data-product-handle]');
      if (card && card.getAttribute) return card.getAttribute('data-product-handle');
      const link = itemEl.querySelector('a[href*="/products/"]');
      if (link){
        try{
          const u=new URL(link.getAttribute('href'), window.location.origin);
          const parts=u.pathname.split('/').filter(Boolean);
          return parts[parts.length-1]||null;
        }catch(e){}
      }
      return null;
    }

    _isUniversalItem(itemEl){
      const card = itemEl.querySelector('[data-fitment]');
      const fit  = card ? card.getAttribute('data-fitment') : 'vehicle';
      return fit === 'universal';
    }

    _buildPageUrlPattern(exampleHref){
      return function(pageNum){
        let url = exampleHref;
        if (url.indexOf('page=') >= 0) {
          return url.replace(/([?&])page=\d+/, '$1page=' + pageNum);
        } else {
          let hash = '';
          const hashIdx = url.indexOf('#');
          if (hashIdx >= 0) { hash = url.slice(hashIdx); url = url.slice(0, hashIdx); }
          const sep = url.indexOf('?') >= 0 ? '&' : '?';
          return url + sep + 'page=' + pageNum + hash;
        }
      };
    }

    _updatePaginationControlsFromTemplate(fragment, silent){
      const nextPagination = fragment.querySelector('.js-pagination');
      const nextBtn = nextPagination ? nextPagination.querySelector('.js-pagination__next-button[href]') : null;
      if (nextPagination && nextBtn) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this._refreshNextButton();
      } else if (!silent) {
        this._cleanupAfterLastPage();
      }
    }

    _refreshNextButton(){
      if (this.nextButton && this.clickHandler) this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      if (this.nextButton) this.nextButton.addEventListener('click', this.clickHandler);
    }

    _cleanupAfterLastPage(){
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      clearInterval(this.nextButtonTimer);
      if (this.paginationContainer) this.paginationContainer.remove();
      if (this.prefetchAll && this._universalQueueFrag && this._universalQueueFrag.childNodes.length) {
        this.universalBucket.appendChild(this._universalQueueFrag);
        this._universalQueueFrag = null;
      }
      const onlyWhenPaginated = this.querySelectorAll('.js-when-paginated-only');
      for (let i=0;i<onlyWhenPaginated.length;i++){
        const n = onlyWhenPaginated[i];
        if (n.parentNode) n.parentNode.removeChild(n);
      }
    }

    _afterAppend(){
      try{ if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(); }catch(e){}
      try{ if (window.renderSelects) window.renderSelects(); }catch(e){}
      try{ if (window.loadDesktopOnlyTemplates) window.loadDesktopOnlyTemplates(); }catch(e){}
      try{ if (window.stickersReinit) window.stickersReinit(); }catch(e){}

      // Keep promo locked even if other scripts tweak the DOM
      this._repositionPromoToVehicleIndex(2);
    }

    async _fetchText(url){
      const res = await fetch(url, { credentials:'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
      return res.text();
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}

/* ================== INLINE PROMO: CLONE + HARD LOCK AT #3 ==================
   Puts the inline promo card as the 3rd grid item (after 2 product cards),
   even if other scripts re-order later:
   - Clones the promo into position #3 and hides the original
   - Re-enforces on any DOM changes (MutationObserver)
   - Skips search templates and banner/top promos
============================================================================= */
(function(){
  var body = document.body;
  if (!body || !body.classList.contains('template-collection') || body.classList.contains('template-search')) return;

  function host(){ return document.querySelector('infinite-scroll') || document; }
  function itemsContainer(h){
    var any = (h || host()).querySelector('.js-pagination__item');
    return any ? any.parentElement : null;
  }
  function directItems(container){
    return Array.prototype.slice.call(container.querySelectorAll(':scope > .js-pagination__item'));
  }
  function isProductItem(el){
    return !!el.querySelector('[data-product-handle]');
  }

  // Try to find the existing INLINE promo wrapper (NOT the top/banner promo)
  function findInlinePromoItem(container){
    var items = directItems(container);
    for (var i=0;i<items.length;i++){
      var it = items[i];
      if (isProductItem(it)) continue;

      var promoInner = it.querySelector(
        '.collection-inline-promo .promo-card,' +
        '.collection-inline-promo [data-promo-card],' +
        '[data-inline-promo],' +
        '.promo-card-wrapper,' +
        '.promo-card,' +
        '.promo-image'
      );
      if (!promoInner) continue;

      if (promoInner.closest('.collection-top-promo') || promoInner.closest('.collection-banner')) continue;
      return it;
    }
    // Fallback: first non-product js-pagination__item that isn’t clearly a banner/top block
    for (var j=0;j<items.length;j++){
      var it2 = items[j];
      if (isProductItem(it2)) continue;
      if (it2.querySelector('.collection-top-promo, .collection-banner')) continue;
      return it2;
    }
    return null;
  }

  function desiredBeforeNode(container){
    // After first two *product* cards
    var items = directItems(container);
    var prodSeen = 0;
    for (var i=0;i<items.length;i++){
      if (isProductItem(items[i])) {
        prodSeen++;
        if (prodSeen === 2) {
          return items[i+1] || null; // insert AFTER 2nd product
        }
      }
    }
    // If fewer than 2 products, put after the last product (or at end)
    for (var k=items.length-1;k>=0;k--){
      if (isProductItem(items[k])) return items[k].nextSibling;
    }
    return null; // end
  }

  var promoState = {
    original: null,   // the theme’s original promo wrapper
    clone:    null    // our locked clone wrapper
  };

  function ensurePromoClone(){
    var cont = itemsContainer();
    if (!cont) return;

    // If we already have a clone but container was replaced, reconnect it
    if (promoState.clone && !document.contains(promoState.clone)) {
      promoState.clone = null;
    }

    // (Re)find original if needed
    if (!promoState.original || !document.contains(promoState.original)) {
      promoState.original = findInlinePromoItem(cont);
    }
    if (!promoState.original) return;

    // Hide the original so external reorders can’t affect visual position
    if (promoState.original.getAttribute('data-promo-original') !== 'true') {
      promoState.original.setAttribute('data-promo-original','true');
      promoState.original.style.display = 'none';
      promoState.original.setAttribute('aria-hidden','true');
    }

    // Create a clone once
    if (!promoState.clone) {
      promoState.clone = promoState.original.cloneNode(true);
      promoState.clone.setAttribute('data-promo-locked','true');
      // Make sure the clone is visible (we hid the original)
      promoState.clone.style.display = '';
      promoState.clone.removeAttribute('aria-hidden');
      // Insert it (we’ll also enforce position below)
      cont.appendChild(promoState.clone);
    }
  }

  function enforcePosition(){
    var cont = itemsContainer();
    if (!cont) return;

    ensurePromoClone();
    if (!promoState.clone) return;

    var beforeNode = desiredBeforeNode(cont);
    // If already there, do nothing
    var currentSiblings = directItems(cont);
    var idx = currentSiblings.indexOf(promoState.clone);
    var desiredIdx = 2; // third slot overall is our target visual goal
    // (But we still compute precise “after 2 products” with beforeNode)

    // Move clone into place
    try {
      cont.insertBefore(promoState.clone, beforeNode);
    } catch(e) {
      // Fallback: append
      cont.appendChild(promoState.clone);
    }
  }

  function start(){
    // Initial and short stabilizer
    enforcePosition();
    requestAnimationFrame(enforcePosition);
    setTimeout(enforcePosition, 80);
    setTimeout(enforcePosition, 180);
    setTimeout(enforcePosition, 360);

    // Ongoing observation: if grid or children change, re-enforce
    var gridObs, hostObs;
    function observeGrid(){
      var cont = itemsContainer();
      if (!cont) return;
      if (gridObs) gridObs.disconnect();
      gridObs = new MutationObserver(function(muts){
        // Any childList change -> enforce
        for (var i=0;i<muts.length;i++){
          if (muts[i].type === 'childList') { enforcePosition(); break; }
        }
      });
      gridObs.observe(cont, { childList:true });
    }

    observeGrid();

    // If the host/grid is replaced, re-attach
    hostObs = new MutationObserver(function(){
      observeGrid();
      enforcePosition();
    });
    hostObs.observe(host(), { childList:true, subtree:true });

    // Also after window load (very late scripts)
    window.addEventListener('load', function(){
      enforcePosition();
      setTimeout(enforcePosition, 0);
      setTimeout(enforcePosition, 200);
      setTimeout(enforcePosition, 600);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
